import React, {
  Dispatch,
  memo,
  SetStateAction,
  useEffect,
  useCallback,
  useMemo
} from "react";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { Button as EDSButton } from "../../../../ui/button";
import {
  DiaryFilterSectonsProp,
  FilterSectionKeys,
  getDefaultDiaryFilterSections,
  IUserAppliedFilterKeys,
  toggleFilter
} from "./DiaryFilterSections";

interface IDiaryFilters {
  selectedFilters: ISelectedFilters;
  userAppliedFilterKeys: IUserAppliedFilterKeys;
  setUserAppliedFilterKeys: Dispatch<SetStateAction<IUserAppliedFilterKeys>>;
}
export interface ISelectedFilters {
  bookingTypes: Array<string>;
  bookingZones: Array<string>;
  bookingAttributes: Array<string>;
  timeOfDay: Array<string>;
  count: number;
}

const DiaryFilters = ({
  selectedFilters,
  userAppliedFilterKeys,
  setUserAppliedFilterKeys
}: IDiaryFilters) => {
  const { venueConfig } = useDiaryContext();

  const diaryFilterSections = useMemo(() => {
    if (!venueConfig) return [];
    return getDefaultDiaryFilterSections(venueConfig);
  }, [venueConfig]);

  const handleToggleFilter = useCallback(
    (sectionKey: FilterSectionKeys, optionKey: string) => {
      toggleFilter({
        sectionKey,
        optionKey,
        userAppliedFilterKeys,
        diaryFilterSections,
        setUserAppliedFilterKeys
      });
    },
    [userAppliedFilterKeys, diaryFilterSections, setUserAppliedFilterKeys]
  );

  useEffect(() => {
    if (selectedFilters.count === 0) {
      setUserAppliedFilterKeys({});
    }
  }, [selectedFilters, setUserAppliedFilterKeys]);

  const renderFilterOption = useCallback(
    (section: DiaryFilterSectonsProp, option: any) => {
      const isSelected = userAppliedFilterKeys?.[section.key]?.has(option.key);
      return (
        <EDSButton
          key={section.key + option.key}
          variant={isSelected ? "default" : "outline"}
          size="sm"
          onClick={() => handleToggleFilter(section.key, option.key)}
          className="justify-start"
        >
          {option.title}
        </EDSButton>
      );
    },
    [userAppliedFilterKeys, handleToggleFilter]
  );

  return (
    <div className="space-y-6">
      {diaryFilterSections.map((section: DiaryFilterSectonsProp) => (
        <div key={section.title} className="space-y-3">
          <h3 className="text-sm font-medium text-foreground">
            {section.title}
          </h3>
          <div className="grid grid-cols-2 gap-panel-padding lg:grid-cols-3 lg:gap-2">
            {section.options.map((option) =>
              renderFilterOption(section, option)
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default memo<IDiaryFilters>(DiaryFilters);
